import { Modal } from 'react-bootstrap';
import { CloseIcon } from '../../bombiix-icon';

export interface BombiixModalProps {
  show: boolean;
  title: React.ReactNode;
  onClose: () => void;
  /** Handler for the header Save button. Omit or pass `hideSave` to hide it. */
  onSave?: () => void;
  /** Text on the Save button. Defaults to "Save". */
  saveLabel?: string;
  /** Color variant for the Save button — maps to `.btn-{variant}` in `_button.scss`. Defaults to `blue`. */
  saveVariant?: 'blue' | 'dark' | 'red' | 'light';
  saveDisabled?: boolean;
  /** Reserved for a future spinner state — currently only disables the button. */
  saveLoading?: boolean;
  /** Hides the header Save button — use for read-only / info modals. */
  hideSave?: boolean;
  /** Hides the header Close button — use for blocking flows that must resolve via Save. */
  hideClose?: boolean;
  /** Maps to `.bombiix-modal--{size}` width tokens. Defaults to `md`. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Passed through to react-bootstrap `Modal.backdrop`. Defaults to `'static'`. */
  backdrop?: 'static' | boolean;
  /**
   * Set false when the body holds a control whose menu portals to `<body>` —
   * a `NewSelectMultiSelect` with `menuPortalTarget`, say. The menu then sits
   * outside the dialog, and react-bootstrap's focus trap would pull focus off
   * its in-menu search the moment the user clicked into it.
   */
  enforceFocus?: boolean;
  children: React.ReactNode;
  /**
   * Optional footer slot for legacy consumers who need extra buttons below
   * the body. Omit for the pure top-bar layout described in the Figma spec.
   */
  footer?: React.ReactNode;
}

/**
 * BombiixModal — Bombiix master-design modal chrome (Figma node 5030:406586).
 * Wraps `react-bootstrap` `<Modal>` for backdrop / ESC / focus-trap handling,
 * but swaps its default header for a full-width action bar: blue Save on the
 * left, centered title, red Close (✕) on the right. Body content is passed
 * as children; an optional `footer` slot exists for legacy layouts.
 * Styling lives in `assets/scss/master-design/_modal.scss` — no per-component CSS.
 */
function BombiixModal({
  show,
  title,
  onClose,
  onSave,
  saveLabel,
  saveVariant = 'blue',
  saveDisabled,
  saveLoading,
  hideSave,
  hideClose,
  size,
  className,
  backdrop,
  enforceFocus,
  children,
  footer,
}: BombiixModalProps) {
  const rootClass = ['bombiix-modal', `bombiix-modal--${size ?? 'md'}`, className].filter(Boolean).join(' ');
  return (
    <Modal show={show} onHide={onClose} backdrop={backdrop ?? 'static'} enforceFocus={enforceFocus} className={rootClass} centered>
      <div className="bombiix-modal__bar">
        {!hideSave ? (
          <button type="button" className={`bombiix-btn btn-${saveVariant} bombiix-modal__save`} onClick={onSave} disabled={saveDisabled || saveLoading}>
            {saveLabel ?? 'Save'}
          </button>
        ) : (
          <span className="bombiix-modal__spacer" aria-hidden="true" />
        )}
        <div className="bombiix-modal__title">{title}</div>
        {!hideClose ? (
          <button type="button" className="bombiix-modal__close" onClick={onClose} aria-label="Close">
            <CloseIcon size={20} />
          </button>
        ) : (
          <span className="bombiix-modal__spacer" aria-hidden="true" />
        )}
      </div>
      <div className="bombiix-modal__body">{children}</div>
      {footer ? <div className="bombiix-modal__footer">{footer}</div> : null}
    </Modal>
  );
}

export default BombiixModal;
